import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { isIdPrismSsoEnabled, isSsoEnabled } from '../config/appConfig';

interface LoginScreenProps {
    /** Open the request-access form (new supplier onboarding request). */
    onRequestAccess: () => void;
}

/**
 * Sign-in is a provider chooser — there is no local password form. Suppliers
 * authenticate through IdPrism and employees through Midway; the actual
 * credential entry (password / OTP / passkey) happens on those hosted pages,
 * not here. A new supplier with no account yet uses "Request access" to submit
 * an onboarding request an approver reviews.
 */
export default function LoginScreen({ onRequestAccess }: LoginScreenProps) {
    const [error, setError] = useState<string | null>(null);

    const startSupplier = () => {
        setError(null);
        getAuthService()
            .signInWithIdPrism()
            .catch((e) => setError(e instanceof Error ? e.message : 'Supplier sign in failed.'));
    };

    const startEmployee = () => {
        setError(null);
        getAuthService()
            .signInWithSso()
            .catch((e) => setError(e instanceof Error ? e.message : 'Employee sign in failed.'));
    };

    return (
        <AuthCard title="Sign in" subtitle="Choose how to access Supplier Gateway">
            <SpaceBetween size="l">
                {error ? <Alert type="error">{error}</Alert> : null}

                {isIdPrismSsoEnabled() ? (
                    <Button variant="primary" fullWidth onClick={startSupplier}>
                        Supplier sign in
                    </Button>
                ) : null}

                {isSsoEnabled() ? (
                    <Button fullWidth onClick={startEmployee}>
                        Amazon employee sign in
                    </Button>
                ) : null}

                <Box textAlign="center">
                    <Button variant="link" onClick={onRequestAccess}>
                        New supplier? Request access
                    </Button>
                </Box>
            </SpaceBetween>
        </AuthCard>
    );
}
